import type { SelectOption } from 'naive-ui';
import type { ContractManagementEntityType } from './detailConfig';
import {
  ClauseCategory,
  ContractManagementObligationType,
  ContractManagementMatterType,
  AccessVisibility,
} from '@/api';
import { enumToSelectOptions } from '@/utils/enumUtils';

/**
 * Edit Field Configuration
 * Defines the structure for form fields in the edit dialog
 */
export interface EditFieldConfig {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'date' | 'email' | 'select' | 'switch' | 'tags';
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  /**
   * Fixed height of a `textarea`, in rows — except in `GaiaEntityFormDialog`,
   * which reads it as an autosize *minimum* and grows to 8. Named for the two
   * dialogs that got here first; check which renderer you are in before
   * assuming a height.
   */
  rows?: number;
  /**
   * Upper-cases as you type and on submit — country codes. Declared here rather
   * than matched on `key`, which is how `GaiaEntityFormDialog` used to spot the
   * ISO field: twice, by name, in two different places.
   */
  upper?: boolean;
  // For select type fields
  options?: SelectOption[];
  // Custom validation rule for this field
  validator?: (value: unknown) => boolean | Error;
  // Custom transform function for the value before submission
  transform?: (value: unknown) => unknown;
  enabledLabel?: string;
  enabledValue?: string;
  disabledLabel?: string;
  disabledValue?: string;
}

/**
 * Edit Dialog Configuration
 * Configuration for each entity type's edit dialog
 */
export interface EditDialogConfig {
  entityType: ContractManagementEntityType;
  title: string;
  fields: EditFieldConfig[];
}

/**
 * Generate clause category options from enum
 * Automatically reflects changes when ClauseCategory enum is updated
 */
export function getClauseCategoryOptions(): SelectOption[] {
  return enumToSelectOptions(ClauseCategory);
}

/**
 * Generate obligation type options from enum
 * Automatically reflects changes when ContractManagementObligationType enum is updated
 */
export function getObligationTypeOptions(): SelectOption[] {
  return enumToSelectOptions(ContractManagementObligationType);
}

/**
 * Generate matter type options from enum
 * Automatically reflects changes when ContractManagementMatterType enum is updated
 */
export function getMatterTypeOptions(): SelectOption[] {
  return enumToSelectOptions(ContractManagementMatterType);
}

/**
 * Configuration for all contract management entity edit dialogs
 * This centralizes the field configurations and makes it easy to add new entity types
 */
// Partial: contractual parties edit through the list config's kind-aware form
// (`EntityFormDialog`), not this fixed field set.
export const entityEditConfigs: Partial<Record<ContractManagementEntityType, EditDialogConfig>> = {
  clause: {
    entityType: 'clause',
    title: 'Edit Clause',
    fields: [
      {
        key: 'title',
        label: 'Title',
        type: 'text',
        required: true,
        placeholder: 'Enter clause title',
      },
      {
        key: 'text',
        label: 'Text',
        type: 'textarea',
        required: true,
        placeholder: 'Enter clause text',
        rows: 4,
      },
      {
        key: 'category',
        label: 'Category',
        type: 'select',
        required: true,
        placeholder: 'Select category',
        options: getClauseCategoryOptions(),
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Enter description (optional)',
        rows: 3,
      },
      {
        key: 'clause_number',
        label: 'Clause Number',
        type: 'text',
        required: false,
        placeholder: 'Enter clause number (optional)',
      },
      {
        key: 'section_path',
        label: 'Section Path',
        type: 'text',
        required: false,
        placeholder: 'Enter section path (optional)',
      },
    ],
  },
  obligation: {
    entityType: 'obligation',
    title: 'Edit Obligation',
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'Enter obligation name',
      },
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        placeholder: 'Select obligation type',
        options: getObligationTypeOptions(),
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Enter description (optional)',
        rows: 3,
      },
      {
        key: 'trigger_event',
        label: 'Trigger Event',
        type: 'text',
        required: false,
        placeholder: 'Enter trigger event (optional)',
      },
    ],
  },
  view: {
    entityType: 'view',
    title: 'Edit View',
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'Enter view name',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Enter description (optional)',
        rows: 4,
      },
      {
        key: 'visibility',
        label: 'Visibility',
        type: 'switch',
        required: true,
        enabledLabel: 'Public',
        enabledValue: AccessVisibility.VISIBLE,
        disabledLabel: 'Private',
        disabledValue: AccessVisibility.PRIVATE,
      },
    ],
  },
  matter: {
    entityType: 'matter',
    title: 'Edit Matter',
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'Enter matter name',
      },
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        placeholder: 'Select matter type',
        options: getMatterTypeOptions(),
      },
      {
        // Matters are the one entity with tags. They can be set when the matter
        // is created and shown as a list column, so the edit dialog has to carry
        // them too — otherwise the only way to change a tag is to delete the
        // matter and make a new one.
        key: 'tags',
        label: 'Tags',
        type: 'tags',
        required: false,
        placeholder: 'Add a tag',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Enter description (optional)',
        rows: 4,
      },
    ],
  },
};

/**
 * Get edit dialog configuration for a specific entity type
 */
export function getEntityEditConfig(entityType: string): EditDialogConfig | undefined {
  return entityEditConfigs[entityType as ContractManagementEntityType];
}

/**
 * Get field configurations for a specific entity type
 */
export function getEntityEditFields(entityType: string): EditFieldConfig[] {
  const config = getEntityEditConfig(entityType);
  return config?.fields ?? [];
}

/**
 * Get dialog title for a specific entity type
 */
export function getEntityEditTitle(entityType: string): string {
  const config = getEntityEditConfig(entityType);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return config?.title || 'Edit Entity';
}

/**
 * Get create dialog title for a specific entity type
 */
export function getEntityCreateTitle(entityType: string): string {
  const editTitle = getEntityEditTitle(entityType);
  if (editTitle.startsWith('Edit ')) {
    return `Create ${editTitle.slice(5)}`;
  }

  return 'Create Entity';
}
